import axios from "axios";
import env from "../variables/config";

axios.defaults.baseURL = env.apiUrl;

const config = () => ({
    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
});

/** Mirrors `services/linkedin/prospecting.ts` in crm-back — one vocabulary, both ends. */
export type BuyingRole = "owner" | "fnb" | "chef" | "buyer" | "sous_chef";
/** "none" = no buying signal in the window. */
export type SignalKey = "opening" | "new_site" | "owner_change" | "none";
export type Scope = "discover" | "invited" | "accepted" | "messaged" | "replied" | "meeting" | "signed" | "followup";

export interface ProspectFilters {
    signals?: SignalKey[];
    /** Department codes; a region is expanded into its departments before the call. */
    departments?: string[];
    /** Keys of NB_ETAB_BUCKETS (lib/nbEtabBuckets). */
    nbEtab?: string[];
    scope?: Scope;
    /** A CRM tag: only prospects already in the CRM carry one. */
    tag?: string;
}

export interface ProspectSignal {
    type: string;
    label: string;
    date: string;
    tone: "hot" | "job" | "general";
    establishmentName: string;
}

export interface ScoreFactor {
    label: string;
    points: number;
}

export interface ProspectState {
    /** Signed. */
    customer: boolean;
    customerAt: string | null;
    invite: {
        status: "queued" | "sent";
        queuedFor?: string;
        sentAt?: string;
        acceptedAt?: string;
        messagedAt?: string;
        repliedAt?: string;
        meetingAt?: string;
        /** The note as it was sent. */
        note?: string;
    } | null;
    followUpAt: string | null;
    /** The rep's own free note. */
    memo: string | null;
}

export interface Portfolio {
    sites: number;
    cities: string[];
}

export interface ProspectRow {
    personId: string;
    name: string;
    headline: string | null;
    linkedinUrl: string;
    role: BuyingRole;
    age: number | null;
    establishment: EstablishmentCard;
    /** The person's own phone if known, else their establishment's. */
    phone: string | null;
    portfolio: Portfolio;
    signals: ProspectSignal[];
    score: number;
    factors: ScoreFactor[];
    state: ProspectState;
    /** The rep's CRM prospect with the same LinkedIn profile, if any. */
    crm: CrmLink | null;
    /** Invited outside the page, someone the base doesn't hold: no portfolio, no score. */
    external?: boolean;
}

export interface CrmLink {
    id: string;
    status: string;
    tags: string[];
}

/** The establishment shown for a prospect, with what a message can say about it. */
export interface EstablishmentCard {
    _id: string;
    name: string;
    city: string | null;
    type?: string | null;
    phone?: string | null;
    website?: string | null;
    rating?: number | null;
    reviews?: number | null;
}

export interface SearchResult {
    rows: ProspectRow[];
    total: number;
    page: number;
    totalPages: number;
}

export interface TimelineItem {
    at: string;
    kind: "signal" | "rep";
    label: string;
    detail?: string;
}

export interface ProspectDetail {
    personId: string;
    name: string;
    linkedinUrl: string;
    age: number | null;
    portfolio: Portfolio;
    signals: ProspectSignal[];
    establishment: EstablishmentCard | null;
    headline: string | null;
    phone: string | null;
    timeline: TimelineItem[];
    state: ProspectState;
    note: string | null;
    external?: boolean;
}

/** `n` of `base` reached the step; `rate` in %, null with no base. */
export interface FunnelRate {
    n: number;
    base: number;
    rate: number | null;
}

export interface Overview {
    quota: { sentToday: number; limit: number; queued: number };
    /** Percentages over invitations sent; null before the first one. */
    /** Each step over the one before (`base`); `overall` = signatures over invitations sent. */
    rates: { sent: number; acceptance: FunnelRate; reply: FunnelRate; meeting: FunnelRate; signature: FunnelRate; overall: FunnelRate };
    counts: {
        invited: number;
        accepted: number;
        messaged: number;
        replied: number;
        meeting: number;
        signed: number;
        followup: number;
        followupDue: number;
    };
}

/** Filters as the API reads them: arrays comma-joined, empties dropped. */
export function toQuery(filters: ProspectFilters): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === "") continue;
        if (Array.isArray(value)) {
            if (value.length) out[key] = value.join(",");
        } else out[key] = String(value);
    }
    return out;
}

export const searchProspects = async (filters: ProspectFilters, page = 1): Promise<SearchResult> => {
    const res = await axios.get("/linkedin/prospects", { ...config(), params: { ...toQuery(filters), page } });
    return res.data;
};

/** For the kanban: the LinkedIn stage of each CRM prospect followed there, by CRM prospect id. */
export const getCrmStages = async (): Promise<Record<string, { stage: Exclude<Scope, "followup">; followUpAt: string | null }>> => {
    const res = await axios.get("/linkedin/crm-stages", config());
    return res.data;
};

/** Sets a CRM card's LinkedIn stage from the CRM (matched on its profile URL). */
export const setStageFromCrm = async (
    prospectId: string,
    stage: Exclude<Scope, "followup">
): Promise<{ stage: Exclude<Scope, "followup">; followUpAt: string | null }> => {
    const res = await axios.post(`/linkedin/crm/${prospectId}/stage`, { stage }, config());
    return res.data;
};

export const getProspect = async (personId: string): Promise<ProspectDetail> => {
    const res = await axios.get(`/linkedin/prospects/${personId}`, config());
    return res.data;
};

export interface InviteResult {
    state: ProspectState;
    sentToday: number;
    limit: number;
}

export const invite = async (personId: string, note: string): Promise<InviteResult> => {
    const res = await axios.post(`/linkedin/prospects/${personId}/invite`, { note }, config());
    return res.data;
};

export const sendQueued = async (personId: string): Promise<InviteResult & { note: string }> => {
    const res = await axios.post(`/linkedin/prospects/${personId}/send-queued`, {}, config());
    return res.data;
};

/** `at: null` clears the follow-up. */
export const setFollowUp = async (personId: string, at: string | null): Promise<ProspectState> => {
    const res = await axios.post(`/linkedin/prospects/${personId}/follow-up`, { at }, config());
    return res.data.state;
};

/** Empty text clears the note. */
export const setMemo = async (personId: string, text: string): Promise<ProspectState> => {
    const res = await axios.post(`/linkedin/prospects/${personId}/memo`, { text }, config());
    return res.data.state;
};

/** One stage ticked or unticked; the backend keeps the funnel ordered. */
export type Outcome = { accepted?: boolean; messaged?: boolean; replied?: boolean; meeting?: boolean; signed?: boolean };

export const setOutcome = async (
    personId: string,
    outcome: Outcome
): Promise<ProspectState> => {
    const res = await axios.post(`/linkedin/prospects/${personId}/outcome`, outcome, config());
    return res.data.state;
};

/** A LinkedIn invitation template — an ordinary EmailTemplate of type "linkedin". */
export interface LinkedinTemplate {
    _id: string;
    name: string;
    text: string;
    type: "linkedin";
    lang: "fr" | "en";
    /** Which prospect situation it is written for; absent on the rep's own templates. */
    linkedinAngle?: "opening" | "owner_change" | "new_site" | "portfolio" | "generic";
    /** When it is sent; absent = an invitation note. */
    linkedinMoment?: LinkedinMoment;
    /** A seeded default never edited; anything else is the rep's own. */
    isDefault?: boolean;
    updatedAt?: string;
}

/** An invitation note, or a message once connected. */
export type LinkedinMoment = "invitation" | "first_message" | "followup" | "meeting_ask" | "after_meeting";

/** The rep's LinkedIn templates; the first call seeds the defaults. Edits go through /email-templates. */
export const getLinkedinTemplates = async (): Promise<LinkedinTemplate[]> => {
    const res = await axios.get("/linkedin/templates", config());
    return res.data;
};

/** Profile links invited outside the page, comma- or line-separated. */
export const addExternalInvites = async (
    text: string
): Promise<{ added: number; matched: number; already: number; invalid: string[] }> => {
    const res = await axios.post("/linkedin/external-invites", { text }, config());
    return res.data;
};

export const syncToCrm = async (personIds: string[]): Promise<{ people: number; establishments: number; listName: string }> => {
    const res = await axios.post("/linkedin/sync", { personIds }, config());
    return res.data;
};

export const getOverview = async (): Promise<Overview> => {
    const res = await axios.get("/linkedin/overview", config());
    return res.data;
};
